import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@components/layout/Navbar";
import TabSelector from "@components/layout/TabSelector";
import EmptyState from "@components/common/EmptyState";
import PatientCard from "@components/cards/PatientCard";
import FormCard from "@components/cards/FormCard";
import ModalDelete from "@components/common/ModalDelete";
import { getAllPatients } from "@services/patient";
import { getAllTemplates } from "@services/template";
import { deletePatient } from "@services/patient";
import { deleteTemplate } from "@services/template";
import VoicePage from "@pages/VoicePage";
import { canAccess } from "@utils/access";

export default function Patients() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab =
    location.state?.defaultTab === "Form Library"
      ? "Form Library"
      : "All Patients";
  const [tab, setTab] = useState(initialTab);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [patients, setPatients] = useState([]);
  const [forms, setForms] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const is_voice = localStorage.getItem("is_voice") === "true";
  const canClinical = canAccess("clinical");

  useEffect(() => {
    if (!canClinical) return;
    getAllPatients()
      .then((data) => setPatients(data.data))
      .catch((err) => console.error(err));
    getAllTemplates()
      .then((data) => setForms(data.data))
      .catch((err) => console.error(err));
  }, [canClinical]);

  const handleDeleteClick = (item) => {
    setOpenMenuIndex(null);

    if ("first_name" in item && "last_name" in item) {
      setSelectedPatient(item);
      setSelectedForm(null);
    } else {
      setSelectedForm(item);
      setSelectedPatient(null);
    }

    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedPatient) {
        await deletePatient(selectedPatient.id);
        setPatients((prev) => prev.filter((p) => p.id !== selectedPatient.id));
      } else if (selectedForm) {
        await deleteTemplate(selectedForm.id);
        setForms((prev) => prev.filter((f) => f.id !== selectedForm.id));
      }

      setShowDeleteModal(false);
      setSelectedPatient(null);
      setSelectedForm(null);
    } catch (err) {
      alert(err.message || "Failed to delete.");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedPatient(null);
  };

  const goToProfile = (patient) => {
    navigate("/patient-profile", { state: patient });
  };

  const handleMenuToggle = (index) => {
    setOpenMenuIndex(openMenuIndex === index ? null : index);
  };

  const handleActionSelect = (action) => {
    if (action === "newPatient") {
      window.location.href = "/new-patient";
    } else if (action === "customForm") {
      window.location.href = "/new-form";
    }
  };

  const handleEditForm = (form) => {
    navigate("/new-form", { state: { formToEdit: form } });
  };

  const filteredPatients = patients.filter((p) =>
    `${p.first_name} ${p.last_name}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const filteredForms = forms.filter((f) =>
    (f?.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Restricted access state
  if (!is_voice && !canClinical) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="sticky top-0 z-40">
          <Navbar
            selected="Consultation Recorder"
            showTabs={false}
            showFieldSearch={false}
          />
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="dashboard-card p-8 max-w-md text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
              Access Restricted
            </h3>
            <p className="text-sm text-slate-500" style={{ fontFamily: 'var(--font-body)' }}>
              You don't have permission to view patients or forms. Contact your administrator for access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Voice-only mode
  if (is_voice) {
    return <VoicePage />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <Navbar
          selected={tab}
          onSelect={setTab}
          showTabs={true}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <div className="lg:hidden px-4 py-3 bg-[#0a0f1c] border-t border-white/[0.05]">
          <TabSelector selected={tab} onSelect={setTab} />
        </div>
      </div>

      {/* Page Header */}
      <div className="px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1
              className="text-2xl font-bold text-slate-800"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {tab === "All Patients" ? "Patients" : "Form Library"}
            </h1>
            <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: 'var(--font-body)' }}>
              {tab === "All Patients"
                ? `${filteredPatients.length} patient${filteredPatients.length !== 1 ? 's' : ''} registered`
                : `${filteredForms.length} form${filteredForms.length !== 1 ? 's' : ''} available`
              }
            </p>
          </div>

          {/* Quick stats badges */}
          <div className="hidden sm:flex items-center gap-3">
            {tab === "All Patients" && patients.length > 0 && (
              <div className="badge badge-success">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span>Active</span>
              </div>
            )}
            {tab === "Form Library" && forms.filter(f => f.fillable_at_home).length > 0 && (
              <div className="badge badge-success">
                <span>{forms.filter(f => f.fillable_at_home).length} remote-enabled</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-6 lg:px-8 pb-28">
        {tab === "All Patients" &&
          (filteredPatients.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredPatients.map((patient, index) => (
                <PatientCard
                  key={patient.id || index}
                  index={index}
                  name={`${patient.first_name} ${patient.last_name}`}
                  phone={patient.phone_number}
                  isOpen={openMenuIndex === index}
                  onToggle={() => handleMenuToggle(index)}
                  closeMenu={() => setOpenMenuIndex(null)}
                  onClick={() => goToProfile(patient)}
                  onDelete={() => handleDeleteClick(patient)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
              <EmptyState
                image="/Visuals - No Patients yet.svg"
                title="No patients yet"
                subtitle="Start by adding your first patient to keep track of records easily."
              />
            </div>
          ))}

        {tab === "Form Library" &&
          (filteredForms.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredForms.map((form, index) => (
                <FormCard
                  key={form.id || index}
                  index={index}
                  name={form.name}
                  fillable_at_home={form.fillable_at_home}
                  isOpen={openMenuIndex === index}
                  onToggle={() => handleMenuToggle(index)}
                  closeMenu={() => setOpenMenuIndex(null)}
                  onDelete={() => handleDeleteClick(form)}
                  onEdit={() => handleEditForm(form)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
              <EmptyState
                image="/Visuals - No Forms Create yet.svg"
                title="No forms have been created yet"
                subtitle="Create custom forms to streamline your patient intake process."
              />
            </div>
          ))}
      </div>

      {/* Bottom fade overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/80 to-transparent z-40" />

      {/* Fixed bottom action button */}
      <div className="fixed bottom-0 left-0 w-full z-50 px-6 lg:px-8 py-4">
        <div className="max-w-md mx-auto lg:max-w-none">
          <button
            onClick={() =>
              tab === "All Patients"
                ? handleActionSelect("newPatient")
                : handleActionSelect("customForm")
            }
            className="btn-primary w-full flex items-center justify-center gap-2.5 py-3.5 text-base group"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            <svg
              className="w-5 h-5 transition-transform duration-200 group-hover:rotate-90"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M12 4v16m8-8H4"
              />
            </svg>
            {tab === "All Patients" ? "Add New Patient" : "Create Custom Form"}
          </button>
        </div>
      </div>

      {/* Delete Modal */}
      <ModalDelete
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        patientName={
          selectedPatient
            ? `${selectedPatient.first_name} ${selectedPatient.last_name}`
            : selectedForm
            ? selectedForm.name
            : ""
        }
        contect={
          selectedPatient
            ? "This action is permanent. Deleting this patient will permanently remove all records and cannot be undone."
            : "This action cannot be undone. Once deleted, the form and all its questions will be permanently removed."
        }
      />
    </div>
  );
}
